import axios, { AxiosError, AxiosRequestHeaders, InternalAxiosRequestConfig } from "axios";
import { refreshToken } from "./auth";

const ApiInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Accept-Language": "ru",
  },
});

const noTokenRequiredUrls = ["/auth/v1/login", "/auth/v1/refresh"];

// Intercepting token
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: string) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });

  failedQueue = [];
};
ApiInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (!noTokenRequiredUrls.some((url) => config.url?.includes(url))) {
      const token = localStorage.getItem("accessToken");
      if (token) {
        const headers = (config.headers ?? {}) as AxiosRequestHeaders;
        headers.Authorization = `Bearer ${token}`;
        config.headers = headers;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);
ApiInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async function (error: unknown) {
    const axiosError = error as AxiosError;
    const originalRequest = (axiosError.config ?? {}) as (InternalAxiosRequestConfig & {
      _retry?: boolean;
    });

    const requestUrl = originalRequest.url ?? "";

    // If refresh itself failed, tokens are invalid -> force logout.
    if (requestUrl.includes("/refresh")) {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      window.location.href = "/";
      return Promise.reject(axiosError);
    }

    // Network/CORS errors (no response)
    if (!axiosError.response) {
      return Promise.reject(axiosError);
    }

    if (axiosError.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            const headers = (originalRequest.headers ?? {}) as AxiosRequestHeaders;
            headers.Authorization = "Bearer " + token;
            originalRequest.headers = headers;
            return ApiInstance(originalRequest);
          })
          .catch((err: unknown) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      return new Promise(function (resolve, reject) {
        refreshToken()
          .then((res: any) => {
            const accessHash = res?.access?.hash as string | undefined;
            const refreshHash = res?.refresh?.hash as string | undefined;
            if (!accessHash || !refreshHash) throw new Error("Invalid refresh response");

            localStorage.setItem("accessToken", accessHash);
            localStorage.setItem("refreshToken", refreshHash);

            const headers = (originalRequest.headers ?? {}) as AxiosRequestHeaders;
            headers.Authorization = "Bearer " + accessHash;
            originalRequest.headers = headers;

            processQueue(null, accessHash);
            resolve(ApiInstance(originalRequest));
          })
          .catch((err: unknown) => {
            processQueue(err, null);
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            reject(err);
          })
          .finally(() => {
            isRefreshing = false;
          });
      });
    }

    return Promise.reject(axiosError);
  }
);

export default ApiInstance;
export { ApiInstance as Api };
